import axios from 'axios';
import {
  ApiEmptyResponse,
  ApiResponse,
  ResponseData,
  ErrorResponse
} from './models';
import { removeItem } from './local.service';

const api = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}`,
  withCredentials: true
});

// Add a response interceptor to fetch the CSRF token
api.interceptors.response.use(
  (response) => {
    // Assuming the CSRF token is sent in a response header or body
    const csrfToken =
      response.headers['csrf-token'] ||
      response.data?.responseData?.csrfTokenValue;

    if (csrfToken) {
      // Store the token in memory or local storage if needed
      api.defaults.headers.common['CSRF-TOKEN'] = csrfToken;
    }

    return response;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Set up a response interceptor
api.interceptors.response.use(
  (response) => response, // Return response if successful
  (error) => {
    const { status } = error.response;
    const request = error.config;
    if (request.headers['Skip-Auth-Interceptor']) {
      // If the request header indicates that it should skip auth handling, just reject the promise
      return Promise.reject(error);
    }
    if (
      status === 401 ||
      (status === 400 &&
        error.response.data === 'CSRF token validation failed.')
    ) {
      removeItem('user');
      removeItem('info');
      // Unauthorized, handle the expired session
      if (window.location.pathname !== '/Account/Login') {
        window.location.href = `/Account/Login?returnUrl=${encodeURIComponent(window.location.pathname)}`;
      } else {
        window.location.href = `/Account/Login?failed=true`;
      }
    }
    return Promise.reject(error);
  }
);

/*
// Add a request interceptor
api.interceptors.request.use(
    (config) => {
        const token = getItem('accessToken');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Add a response interceptor
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // If the error status is 401 and there is no originalRequest._retry flag,
        // it means the token has expired and we need to refresh it
        if (error.response.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            try {
                const refreshToken = getItem('refreshToken');
                const response = await axios.post(`${process.env.REACT_APP_API_URL}/refresh`, { refreshToken });
                const { accessToken, refreshToken: newRefreshToken } = response.data;

                const useLocalStorage = getUseLocalStorage();
                setItem('accessToken', accessToken, useLocalStorage);
                setItem('refreshToken', newRefreshToken, useLocalStorage);

                // Retry the original request with the new token
                originalRequest.headers.Authorization = `Bearer ${accessToken}`;
                return axios(originalRequest);
            } catch (error) {
                // Handle refresh token error or redirect to login
            }
        }

        return Promise.reject(error);
    }
);
*/
export const getData = async <T>(
  url: string,
  skipAuth?: boolean
): Promise<ResponseData<T>> => {
  try {
    const config = skipAuth
      ? {
          headers: {
            'Skip-Auth-Interceptor': 'true'
          }
        }
      : {};
    const response = await api.get<ApiResponse<T>>(url, config);
    return { success: true, data: response.data.responseData, errors: [] };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    return {
      success: false,
      errors: error.response?.data?.errors?.map(
        (p: ErrorResponse) => p.message
      ) ?? ['Unexpected error']
    };
  }
};
export const postData = async <T>(
  url: string,
  body: unknown
): Promise<ResponseData<T>> => {
  try {
    const response = await api.post<ApiResponse<T>>(url, body);
    return {
      success: true,
      data: response.data.responseData,
      errors: [],
      errorDetails: []
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    if (error.response?.status === 403) {
      return {
        success: false,
        errors: ['You need a higher account level to use this feature'],
        errorDetails: [
          {
            message: 'You need a higher account level to use this feature',
            requiresUpgrade: true
          }
        ]
      };
    }

    return {
      success: false,
      // TODO: Replace errors with errorDetails
      errors: error.response?.data?.errors?.map(
        (p: ErrorResponse) => p.message
      ) ?? ['Unexpected error'],
      errorDetails: error.response?.data?.errors?.map((p: ErrorResponse) => ({
        message: p.message,
        requiresUpgrade: p.requiresUpgrade
      })) ?? [{ message: 'Unexpected error' }]
    };
  }
};
export const patchData = async <T>(
  url: string,
  body: unknown
): Promise<ResponseData<T>> => {
  try {
    const response = await api.patch<ApiResponse<T>>(url, body);
    return { success: true, data: response.data.responseData, errors: [] };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    return {
      success: false,
      errors: error.response?.data?.errors?.map(
        (p: ErrorResponse) => p.message
      ) ?? ['Unexpected error']
    };
  }
};
export const postFile = async <T>(
  url: string,
  file: File
): Promise<ResponseData<T>> => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post<ApiResponse<T>>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return { success: true, data: response.data.responseData, errors: [] };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    return {
      success: false,
      errors: error.response?.data?.errors?.map(
        (p: ErrorResponse) => p.message
      ) ?? ['Unexpected error']
    };
  }
};

export const putData = async <T>(
  url: string,
  body: unknown
): Promise<ResponseData<T>> => {
  try {
    const response = await api.put<ApiResponse<T>>(url, body);
    return { success: true, data: response.data.responseData, errors: [] };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    return {
      success: false,
      errors: error.response?.data?.errors?.map(
        (p: ErrorResponse) => p.message
      ) ?? ['Unexpected error']
    };
  }
};
export const deleteData = async (
  url: string
): Promise<ResponseData<ApiEmptyResponse>> => {
  try {
    const response = await api.delete<ApiResponse<ApiEmptyResponse>>(url, {
      data: {}
    });
    return { success: true, data: response.data.responseData, errors: [] };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    return {
      success: false,
      errors: error.response?.data?.errors?.map(
        (p: ErrorResponse) => p.message
      ) ?? ['Unexpected error']
    };
  }
};

export default api;
